import React, { useEffect, useState } from "react";
import { Text, Button, Box, Toggle, useToast, Icon } from "@familyzone/component-library";
import { Feedback } from "./Feedback";
import PageWithHeader from "../../templates/PageWithHeader";
import { useAcademyDeviceConfigStore } from "../../../storez/AcademyDeviceConfigStore";
import posthog from "posthog-js";
import { colors } from "../../../theme";

interface OverviewProps {
  router?: {
    push: (path: string) => void;
  };
}

const Overview: React.FC<OverviewProps> = ({ router }) => {
  const deviceConfig = useAcademyDeviceConfigStore((state) => state.config);
  const fetchDeviceConfig = useAcademyDeviceConfigStore((state) => state.fetch);
  const saveDeviceConfig = useAcademyDeviceConfigStore((state) => state.save);
  const resetDeviceConfig = useAcademyDeviceConfigStore((state) => state.reset);

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const { errorToast, successToast } = useToast();

  const breadcrumbs = [
    { title: "Product Training", url: "/product-training", isActive: false },
    { title: "Overview", isActive: true },
  ];

  useEffect(() => {
    async function fetchConfig(): Promise<void> {
      setLoading(true);

      try {
        await fetchDeviceConfig();
      } catch (error) {
        console.error("Failed to fetch device config:", error);
      } finally {
        setLoading(false);
      }
    }

    resetDeviceConfig();
    void fetchConfig();
  }, [fetchDeviceConfig, resetDeviceConfig]);

  const handleToggleChange = () => {
    const currentConfig = deviceConfig || { classwize_enabled: false };
    const newConfig = {
      ...currentConfig,
      classwize_enabled: !currentConfig.classwize_enabled,
    };

    // Track PostHog event based on toggle state change
    const eventName = newConfig.classwize_enabled ? "training_enabled" : "training_disabled";
    posthog.capture(eventName, {
      product: "Product Training",
      feature: "Classwize Training",
      previous_state: currentConfig.classwize_enabled,
      new_state: newConfig.classwize_enabled,
    });

    setSaving(true);

    // Save to backend
    saveDeviceConfig(newConfig)
      .then(() => {
        successToast({
          title: "Access updated",
          description: "Access to Classroom Manager training has been updated successfully.",
          position: "top",
          isClosable: true,
        });
      })
      .catch((error) => {
        console.error("Failed to save device config:", error);
        errorToast({
          title: "Error saving device config",
          description: "Failed to save the Classroom Manager Training setting. Please try again.",
          position: "top",
          isClosable: true,
        });
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const handleAccessResources = () => {
    if (router) {
      router.push("/product-training/overview/classwize");
    } else {
      // Fallback to window.location if router is not available
      window.location.href = "/product-training/overview/classwize";
    }
  };

  return (
    <PageWithHeader breadcrumbs={breadcrumbs} title="Product Training">
      <Box sx={{ padding: "12px 24px 24px 24px", display: "flex", width: "100%", flexDirection: "column" }}>
        <Text sx={{ fontSize: "16px", color: colors.neutrals["700"], margin: "16px 0 32px 0" }}>
          Simplify Classroom Manager training with Academy. When turned on, teachers can access training resources directly via the Academy
          button in Classroom Manager.
        </Text>

        <Box
          sx={{
            width: "275px",
            borderRadius: "8px",
            padding: "24px",
            backgroundColor: "white",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Box sx={{ display: "grid", gap: "24px", height: "100%" }}>
            <Box
              sx={{
                backgroundColor: "#E8F8EF",
                borderRadius: "8px",
                height: "120px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i className="fa fa-screen-users" style={{ fontSize: "48px", color: "#58B78E" }} />
            </Box>

            <Box sx={{ display: "grid", gap: "16px" }}>
              <Text fontFamily="heading" fontWeight="500" fontSize="18px">
                Classroom Manager Training
              </Text>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto",
                  alignItems: "center",
                  padding: "8px",
                  borderRadius: "4px",
                }}
              >
                <Toggle
                  data-testid="classwize-training-toggle"
                  labelRight="Available to users"
                  onChange={handleToggleChange}
                  isChecked={deviceConfig?.classwize_enabled || false}
                  disabled={loading || saving}
                  aria-checked={deviceConfig?.classwize_enabled || false}
                />
              </Box>

              <Button
                id="access-resources-button"
                variant="outline"
                rightIcon={<Icon icon="fa-arrow-right" width="16px" height="16px" />}
                width="164px"
                size="sm"
                onClick={handleAccessResources}
              >
                <Text fontSize="14px" fontWeight="500">
                  Access resources
                </Text>
              </Button>
            </Box>
          </Box>
        </Box>
        <Feedback isVisible={true} />
      </Box>
    </PageWithHeader>
  );
};

export default Overview;
